if (!customElements.get('rs7gt-product-rotator')) {
  customElements.define('rs7gt-product-rotator', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.launchRoot = this.closest('rs7gt-launch');
      this.cache = new Map();
      this.generation = (this.generation || 0) + 1;
      this.frameIndex = 0;
      this.frameCount = 36;
      this.colors = JSON.parse(this.querySelector('[data-rotator-config]')?.textContent || '[]');
      this.viewer = this.querySelector('[data-rotator-viewer]');
      this.image = this.querySelector('[data-rotator-image]');
      this.status = this.querySelector('[data-rotator-status]');
      this.feedback = this.querySelector('[data-rotator-feedback]');
      this.message = this.querySelector('[data-rotator-message]');
      this.radios = [...this.querySelectorAll('[data-rotator-color]')];
      this.panels = [...this.querySelectorAll('[data-rotator-purchase]')];
      this.mediaQuery = matchMedia('(max-width: 749px), (max-width: 1099px) and (max-height: 599px)');
      this.selected = this.colors.find(color => this.radios.find(radio => radio.checked)?.value === color.id) || this.colors[0];
      if (!this.viewer || !this.image || !this.selected) return;
      const options = { signal: this.controller.signal };
      this.dataset.enhanced = '';
      this.radios.forEach(radio => { radio.disabled = false; });
      this.viewer.addEventListener('pointerdown', event => this.startDrag(event), options);
      this.viewer.addEventListener('pointermove', event => this.moveDrag(event), options);
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(name => {
        this.viewer.addEventListener(name, event => {
          if (event.pointerId === this.pointerId) this.finishDrag();
        }, options);
      });
      this.viewer.addEventListener('keydown', event => this.keyRotate(event), options);
      this.viewer.addEventListener('dragstart', event => event.preventDefault(), options);
      this.viewer.addEventListener('focus', () => this.ensureLoaded(), options);
      this.viewer.addEventListener('blur', () => this.viewer.removeAttribute('data-pointer-focus'), options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Tab') this.viewer.removeAttribute('data-pointer-focus');
      }, options);
      this.querySelector('[data-rotator-retry]').addEventListener('click', () => this.selectColor(this.pendingColor || this.selected), options);
      this.radios.forEach(radio => {
        const color = this.colors.find(item => item.id === radio.value);
        radio.addEventListener('change', () => this.selectColor(color), options);
        radio.addEventListener('focus', () => this.warmColor(color), options);
        radio.closest('label').addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse') this.warmColor(color);
        }, options);
      });
      this.mediaQuery.addEventListener('change', () => {
        this.finishDrag();
        if (this.started) this.selectColor(this.pendingColor || this.selected);
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.finishDrag();
      }, options);
      window.addEventListener('blur', () => this.finishDrag(), options);
      document.addEventListener('shopify:block:select', event => {
        const color = this.colors.find(item => item.id === event.detail?.blockId);
        if (color) this.selectColor(color);
      }, options);
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.ensureLoaded();
        else this.finishDrag();
      }, { rootMargin: '200px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.finishDrag();
      this.controller?.abort();
      this.observer?.disconnect();
      this.cache?.forEach(entry => entry.controller.abort());
      this.cache?.clear();
      this.frames = null;
      this.activeKey = null;
      this.started = false;
      this.generation++;
      this.controller = null;
      this.viewer?.removeAttribute('data-pointer-focus');
      this.removeAttribute('data-enhanced');
    }

    get resolution() { return this.mediaQuery.matches ? 'mobile' : 'desktop'; }

    ensureLoaded() {
      if (!this.started) this.selectColor(this.selected);
    }

    async warmColor(color) {
      if (!color || navigator.connection?.saveData || color.id === this.selected.id) return;
      if (this.pendingColor && this.pendingColor.id !== color.id) return;
      try { await this.loadSet(color, this.resolution); } catch (_) {}
    }

    async selectColor(color) {
      if (!color) return;
      this.started = true;
      this.finishDrag();
      const angle = this.frameIndex / this.frameCount;
      const generation = ++this.generation;
      this.pendingColor = color;
      this.radios.forEach(radio => { radio.checked = radio.value === this.selected.id; });
      this.setReady(false);
      this.viewer.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loadingLabel;
      if (this.feedback) this.feedback.hidden = false;
      this.setText(this.message, this.dataset.loadingLabel);
      this.querySelector('[data-rotator-retry]').hidden = true;
      try {
        const set = await this.loadSet(color, this.resolution);
        if (!this.isConnected || generation !== this.generation) return;
        this.selected = color;
        this.pendingColor = null;
        this.activeKey = set.key;
        this.frames = set.images;
        this.frameCount = set.images.length;
        this.image.closest('picture')?.querySelectorAll('source').forEach(source => source.remove());
        this.image.width = set.width;
        this.image.height = set.height;
        this.image.alt = color.alt;
        this.renderFrame(Math.round(angle * this.frameCount));
        this.radios.forEach(radio => { radio.checked = radio.value === color.id; });
        this.setText(this.querySelector('[data-rotator-color-label]'), color.label);
        this.panels.forEach(panel => { panel.hidden = panel.dataset.rotatorPurchase !== color.id; });
        this.setReady(true);
        this.status.textContent = this.dataset.readyLabel;
        if (this.feedback) this.feedback.hidden = true;
        this.trimCache(set.key);
      } catch (error) {
        if (!this.isConnected || generation !== this.generation) return;
        this.setReady(!!this.frames);
        this.status.textContent = this.dataset.errorLabel;
        if (this.feedback) this.feedback.hidden = false;
        this.setText(this.message, this.dataset.errorLabel);
        this.querySelector('[data-rotator-retry]').hidden = false;
      } finally {
        if (this.isConnected && generation === this.generation) this.viewer.setAttribute('aria-busy', 'false');
      }
    }

    loadSet(color, resolution) {
      const key = `${color.id}:${resolution}`;
      if (this.cache.has(key)) return this.cache.get(key).promise;
      const controller = new AbortController();
      const entry = { key, controller, images: [] };
      const rootSignal = this.controller.signal;
      const abort = () => controller.abort();
      rootSignal.addEventListener('abort', abort, { once: true });
      controller.signal.addEventListener('abort', () => {
        rootSignal.removeEventListener('abort', abort);
        entry.images.forEach(image => { if (image) image.src = ''; });
        entry.images = [];
      }, { once: true });
      entry.promise = (async () => {
        const manifestURL = new URL(color.manifest, location.href);
        const response = await fetch(manifestURL, { signal: controller.signal });
        if (!response.ok) throw new Error('Frame manifest unavailable');
        const manifest = await response.json();
        const source = manifest[resolution];
        if (!source || !Array.isArray(source.frames) || source.frames.length < 2 || source.frames.length > 120
          || source.frames.length !== manifest.frameCount || !(source.width > 0 && source.height > 0)) {
          throw new Error('Invalid frame manifest');
        }
        entry.width = source.width;
        entry.height = source.height;
        const urls = source.frames.map(name => {
          if (!/^[a-z0-9-]+\.(webp|jpe?g)$/.test(name)) throw new Error('Invalid frame asset');
          const url = new URL(name, manifestURL);
          if (manifestURL.search) url.search = manifestURL.search;
          return url.href;
        });
        let next = 0;
        const worker = async () => {
          while (next < urls.length) {
            controller.signal.throwIfAborted();
            const index = next++;
            entry.images[index] = await this.loadImage(urls[index], controller.signal);
          }
        };
        await Promise.all(Array.from({ length: 4 }, worker));
        return entry;
      })().catch(error => {
        controller.abort();
        if (this.cache.get(key) === entry) this.cache.delete(key);
        throw error;
      });
      this.cache.set(key, entry);
      this.trimCache(key);
      return entry.promise;
    }

    trimCache(keepKey) {
      for (const [key, entry] of this.cache) {
        if (this.cache.size <= 2) break;
        if (key === keepKey || key === this.activeKey) continue;
        entry.controller.abort();
        this.cache.delete(key);
      }
    }

    loadImage(url, signal) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        let finished = false;
        const cleanup = () => {
          image.onload = image.onerror = null;
          signal.removeEventListener('abort', abort);
          clearTimeout(timer);
        };
        const fail = error => {
          if (finished) return;
          finished = true;
          cleanup();
          image.src = '';
          reject(error);
        };
        const abort = () => fail(new DOMException('Cancelled', 'AbortError'));
        const timer = setTimeout(() => fail(new Error('Frame loading timed out')), 15000);
        signal.addEventListener('abort', abort, { once: true });
        image.decoding = 'async';
        image.onerror = () => fail(new Error('Frame unavailable'));
        image.onload = async () => {
          try {
            await image.decode();
            if (finished) return;
            signal.throwIfAborted();
            finished = true;
            cleanup();
            resolve(image);
          } catch (error) { fail(error); }
        };
        if (signal.aborted) abort();
        else image.src = url;
      });
    }

    setReady(ready) {
      this.ready = ready;
      this.viewer.toggleAttribute('data-ready', ready);
      this.viewer.setAttribute('aria-disabled', String(!ready));
    }

    startDrag(event) {
      if (event.button !== 0 || !event.isPrimary) return;
      this.viewer.setAttribute('data-pointer-focus', '');
      if (!this.ready || this.pointerId != null) return;
      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startIndex = this.frameIndex;
      const touch = event.pointerType === 'touch';
      const configured = Number(touch ? this.dataset.touchSensitivity : this.dataset.mouseSensitivity);
      this.sensitivity = Math.max(1, configured || (touch ? 22 : 28));
      this.viewer.setPointerCapture(event.pointerId);
      this.viewer.setAttribute('data-dragging', '');
      this.viewer.focus({ preventScroll: true });
      event.preventDefault();
    }

    moveDrag(event) {
      if (event.pointerId !== this.pointerId) return;
      this.showFrame(this.startIndex - Math.round((event.clientX - this.startX) / this.sensitivity));
      event.preventDefault();
    }

    finishDrag() {
      if (this.renderTask) {
        cancelAnimationFrame(this.renderTask);
        this.renderTask = 0;
        this.renderFrame(this.nextFrame);
      }
      const pointerId = this.pointerId;
      this.pointerId = null;
      if (pointerId != null && this.viewer?.hasPointerCapture(pointerId)) this.viewer.releasePointerCapture(pointerId);
      this.viewer?.removeAttribute('data-dragging');
    }

    keyRotate(event) {
      this.viewer.removeAttribute('data-pointer-focus');
      if (!this.ready) return;
      const step = event.shiftKey ? 3 : 1;
      if (event.key === 'ArrowLeft') this.showFrame(this.frameIndex - step);
      else if (event.key === 'ArrowRight') this.showFrame(this.frameIndex + step);
      else if (event.key === 'Home') this.showFrame(0);
      else if (event.key === 'End') this.showFrame(this.frameCount - 1);
      else return;
      event.preventDefault();
    }

    showFrame(index) {
      if (!this.ready) return;
      this.nextFrame = index;
      if (this.renderTask) return;
      this.renderTask = requestAnimationFrame(() => {
        this.renderTask = 0;
        this.renderFrame(this.nextFrame);
      });
    }

    renderFrame(index) {
      if (!this.frames?.length) return;
      this.frameIndex = ((Math.round(index) % this.frameCount) + this.frameCount) % this.frameCount;
      const frame = this.frames[this.frameIndex];
      if (this.image.src !== frame.src) this.image.src = frame.src;
      const angle = Math.round(this.frameIndex / this.frameCount * 360);
      this.viewer.setAttribute('aria-valuemax', String(Math.round((this.frameCount - 1) / this.frameCount * 360)));
      this.viewer.setAttribute('aria-valuenow', String(angle));
      this.viewer.setAttribute('aria-valuetext', this.dataset.angleLabel.replace('[angle]', angle));
    }

    setText(element, text) {
      if (!element) return;
      const reveal = this.launchRoot?.clockReveal;
      if (reveal?.setText) reveal.setText(element, text);
      else if (element.textContent !== text) element.textContent = text;
    }

  });
}
